import _ from 'lodash';
import { BlockEmbed } from 'quill/blots/block';
import { Delta } from 'quill/core';
import type { Parchment } from 'quill/core';
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQuill } from '../../../QuillContext';
import type { RenderBlock } from '../../../utils/deltaToRenderBlocks';
import { renderBlocksToDelta } from '../../../utils/deltaToRenderBlocks';
import Scroll from '../../Scroll';

interface DelayedRenderProps {
  blot: DelayedRenderBlockGroup;
}

const DelayedRender = ({ blot }: DelayedRenderProps) => {
  const { quill } = useQuill();

  useEffect(() => {
    if (!quill) return undefined;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) {
        observer.disconnect();
        quill.updateContents(
          new Delta()
            .retain(blot.offset(blot.scroll))
            .delete(1)
            .concat(renderBlocksToDelta(blot.renderBlocks)),
        );
      }
    });

    observer.observe(blot.domNode);
    return () => {
      observer.disconnect();
    };
  }, [quill, blot]);

  return <div style={{ height: '100%' }} />;
};

const getEstimatedHeight = (renderBlock: RenderBlock) =>
  renderBlock.type === 'text' ? 40 : 200;

class DelayedRenderBlockGroup extends BlockEmbed {
  static blotName = 'delayed-render-block-group';
  static className = 'ql-delayed-render-block-group';
  static tagName = 'div';

  renderBlocks: RenderBlock[];
  id = _.uniqueId();

  constructor(
    scroll: Parchment.ScrollBlot,
    domNode: Node,
    value: RenderBlock[],
  ) {
    super(scroll, domNode);
    this.renderBlocks = value;
    this.domNode.style.height = `${value.reduce(
      (height, renderBlock) => height + getEstimatedHeight(renderBlock),
      0,
    )}px`;
  }

  attach() {
    super.attach();
    // @ts-expect-error
    this.scroll.emitter.emit(Scroll.events.BLOT_MOUNT, this);
  }

  detach() {
    super.detach();
    // @ts-expect-error
    this.scroll.emitter.emit(Scroll.events.BLOT_UNMOUNT, this);
  }

  renderPortal() {
    return createPortal(<DelayedRender blot={this} />, this.domNode);
  }
}

export default DelayedRenderBlockGroup;
